import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Bookmark } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCity } from "@/contexts/CityContext";
import {
  useSavedFacilities,
  type SavedFacilitiesResponse,
} from "@/lib/board";
import { api } from "@/lib/api";
import type { Facility } from "@/lib/public-directory";

export function SavedFacilityButton({
  facility,
  compact = false,
}: {
  facility: Facility;
  compact?: boolean;
}) {
  const { roles, isAuthenticated } = useAuth();
  const { city } = useCity();
  const queryClient = useQueryClient();
  const isMember =
    isAuthenticated && (roles.includes("gp") || roles.includes("specialist"));
  const savedQuery = useSavedFacilities(city.slug, isMember);
  const queryKey = ["board-saved", city.slug] as const;
  const isSaved =
    savedQuery.data?.items.some((item) => item.id === facility.id) ?? false;

  const mutation = useMutation({
    mutationFn: (save: boolean) =>
      save
        ? api.post<{ ok: true }>(
            `/${city.slug}/v1/board/saved/${encodeURIComponent(facility.id)}`,
          )
        : api.delete<{ ok: true }>(
            `/${city.slug}/v1/board/saved/${encodeURIComponent(facility.id)}`,
          ),
    onMutate: async (save) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<SavedFacilitiesResponse>(queryKey);
      queryClient.setQueryData<SavedFacilitiesResponse>(queryKey, (current) => {
        const items = current?.items ?? [];
        return {
          items: save
            ? items.some((item) => item.id === facility.id)
              ? items
              : [...items, facility]
            : items.filter((item) => item.id !== facility.id),
        };
      });
      return { previous };
    },
    onError: (_error, _save, context) => {
      queryClient.setQueryData(queryKey, context?.previous ?? { items: [] });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: ["board-home", city.slug] });
    },
  });

  if (!isMember) return null;

  return (
    <button
      type="button"
      className={`pb-save-button${compact ? " pb-save-button--compact" : ""}`}
      aria-pressed={isSaved}
      aria-label={`${isSaved ? "Remove" : "Save"} ${facility.name}${
        isSaved ? " from" : " to"
      } saved specialists`}
      disabled={savedQuery.isLoading || mutation.isPending}
      onClick={() => mutation.mutate(!isSaved)}
    >
      <Bookmark size={15} fill={isSaved ? "currentColor" : "none"} aria-hidden="true" />
      {compact ? (isSaved ? "Saved" : "Save") : isSaved ? "Saved specialist" : "Save specialist"}
    </button>
  );
}
